import MonthlyAttReportAllUsersBtn from "components/Pdfs/Attendance/AttByMonthAllUsers/MonthlyAttReportAllUsersBtn";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useState, useCallback, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  calculateTimeDifference,
  convertToDecimalHours,
  getCurTimeOfDeviceTimeZone,
  getDatesArrByMonth,
  getDayNameByDate,
  getTotalHours,
  getTotalLateTime,
  getTotalOvertime,
} from "utils/attendanceUtility";
import {
  getNightUserTotalHours,
  getNightUserTotalLateTime,
  getNightUserTotalOvertime,
} from "utils/NightUserAttendanceUtility";

export default function GenerateMonthlyAttendanceAllUsers({ deviceId }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  const [showModal, setShowModal] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [departments, setDepartments] = useState([]);

  /*   All Functions
   ********************************************* */
  const loadingOn = useCallback(() => {
    setLoading(true);
  }, []);
  const loadingOff = useCallback(() => {
    setLoading(false);
  }, []);

  const handleSelectMonth = (event) => {
    setSelectedReport(null);
    setSelectedMonth(event.target.value);
  };

  const handleSelectAllUsers = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allSelected = filteredUsers.map((user) => user.userId);
      setSelectedUsers(allSelected);
    } else {
      if (filterDepartment) {
        // Deselect only users from current department
        const departmentUserIds = filteredUsers.map((user) => user.userId);
        setSelectedUsers((prevSelected) =>
          prevSelected.filter((id) => !departmentUserIds.includes(id))
        );
      } else {
        // Deselect all users if no department filter
        setSelectedUsers([]);
      }
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        // Deselect user
        const newSelected = prev.filter((id) => id !== userId);
        if (newSelected.length === 0) {
          setSelectAll(false);
        }
        return newSelected;
      } else {
        // Select user
        const newSelected = [...prev, userId];
        if (newSelected.length === allUsers.length) {
          setSelectAll(true);
        }
        return newSelected;
      }
    });
  };

  const getUsersDataByDeviceId = useCallback(
    async (id) => {
      loadingOn();
      try {
        let filterBy = searchParams.get("filterBy") || "active";
        const { data } = await API.post(
          "/device-users-list/" + id,
          { filterBy },
          {
            headers: authHeader(),
          }
        );

        if (data?.success) {
          if (data?.data && data?.data.length > 0) {
            setAllUsers(data.data);
            // Select all users by default
            setSelectedUsers(data.data.map((user) => user.userId));

            // Extract unique departments
            const uniqueDepartments = [
              ...new Set(data.data.map((user) => user.department)),
            ];
            setDepartments(uniqueDepartments);

            setSelectedReport(null);
          }
        }

        loadingOff();
      } catch (err) {
        loadingOff();
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      } finally {
        loadingOff();
      }
    },
    [dispatch, loadingOff, loadingOn, searchParams, setSelectedReport]
  );

  // Get Device Id
  const GetDeviceById = useCallback(async () => {
    try {
      if (!deviceId) {
        return;
      }
      loadingOn();
      const { data } = await API.get("/get-attendance-device/" + deviceId, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedDevice(data.data);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  }, [deviceId, dispatch, loadingOff, loadingOn]);

  // Make Api call to get logs by selected Month for all users
  const getLogsByMonthForAllUsers = async () => {
    try {
      loadingOn();

      if (!selectedDevice?.id || !selectedDevice?.timezone) {
        dispatch(
          setNotification({
            message: "Device not found or timezone not set",
            type: "error",
          })
        );
        loadingOff();
        return;
      }

      const pakistanStartDate = new Date(
        selectedMonth.toLocaleString("en-US", {
          timeZone: selectedDevice?.timezone,
        })
      );
      // set hours to 0, minutes to 0, seconds to 0
      pakistanStartDate.setUTCHours(0, 0, 0, 0);

      // Create end date of selected month for Pakistan timezone
      const pakistanEndDate = new Date(
        pakistanStartDate.getUTCFullYear(),
        pakistanStartDate.getUTCMonth() + 1,
        2
      );
      pakistanEndDate.setUTCHours(23, 59, 59, 999);
      // increment day by 2
      // pakistanEndDate.setUTCDate(pakistanEndDate.getUTCDate() + 2);

      const { data } = await API.post(
        "/get-all-users-logs/by-month",
        {
          deviceId,
          startDate: pakistanStartDate,
          endDate: pakistanEndDate,
          userIds: selectedUsers, // Pass selected user IDs to the API
        },
        {
          headers: authHeader(),
        }
      );

      loadingOff();
      return data;
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Generate report for the selected month
  const GenerateReport = async (e) => {
    setSelectedReport(null);

    if (!selectedMonth) {
      dispatch(
        setNotification({
          message: "Please Select Month",
          type: "error",
        })
      );
      return;
    }

    if (selectedUsers.length === 0) {
      dispatch(
        setNotification({
          message: "Please select at least one user",
          type: "error",
        })
      );
      return;
    }

    // Make API call for the selected month
    const data = await getLogsByMonthForAllUsers();

    if (data?.success) {
      processUsersAttendanceData(data.data, data?.holidaysList);
    } else {
      dispatch(
        setNotification({
          message: data?.message || "Failed to fetch data",
          type: "error",
        })
      );
    }
  };

  const formatUtcTime = (isoString) => {
    if (!isoString) return "";
    const dt = new Date(isoString);
    const hours = String(dt.getUTCHours()).padStart(2, "0");
    const minutes = String(dt.getUTCMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const buildNightShiftSessions = (logs = []) => {
    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.recordTime) - new Date(b.recordTime)
    );

    const dateGroups = {};

    sortedLogs.forEach((log) => {
      const dt = new Date(log.recordTime);
      const hour = dt.getUTCHours();
      const rawDate = log.recordTime.slice(0, 10);

      if (!dateGroups[rawDate]) {
        dateGroups[rawDate] = {
          inRecords: [],
          outRecords: [],
        };
      }

      if (hour >= 12) {
        dateGroups[rawDate].inRecords.push(log);
      } else {
        dateGroups[rawDate].outRecords.push(log);
      }
    });

    const sessionsMap = {};

    Object.entries(dateGroups).forEach(([date, group]) => {
      if (group.inRecords.length > 0) {
        group.inRecords.sort(
          (a, b) => new Date(a.recordTime) - new Date(b.recordTime)
        );
        const earliestIn = group.inRecords[0];

        if (!sessionsMap[date]) {
          sessionsMap[date] = {
            date,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[date].in = earliestIn.recordTime;
        sessionsMap[date].inTimeRecordId = earliestIn.id;
        sessionsMap[date].inLog = earliestIn;
        sessionsMap[date].inRecords = group.inRecords;
      }

      if (group.outRecords.length > 0) {
        group.outRecords.sort(
          (a, b) => new Date(b.recordTime) - new Date(a.recordTime)
        );
        const latestOut = group.outRecords[0];

        const prevDateObj = new Date(date + "T00:00:00Z");
        prevDateObj.setUTCDate(prevDateObj.getUTCDate() - 1);
        const prevDate = prevDateObj.toISOString().slice(0, 10);

        if (!sessionsMap[prevDate]) {
          sessionsMap[prevDate] = {
            date: prevDate,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[prevDate].out = latestOut.recordTime;
        sessionsMap[prevDate].outTimeRecordId = latestOut.id;
        sessionsMap[prevDate].outLog = latestOut;
        sessionsMap[prevDate].outRecords = group.outRecords;
      }
    });

    return Object.values(sessionsMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
  };

  const getNightShiftRecordsByDate = ({ logs = [], userData }) => {
    if (!logs.length || !userData?.weeklySchedule) {
      return {};
    }

    const sessions = buildNightShiftSessions(logs);
    if (!sessions.length) {
      return {};
    }

    const weeklySchedule = JSON.parse(userData?.weeklySchedule || "{}");
    const rules = JSON.parse(userData?.rules || "{}");

    return sessions.reduce((acc, session) => {
      if (!session?.in || !session?.out) {
        return acc;
      }

      const dateReference = new Date(session.in);
      const dayNameOfWeek = getDayNameByDate(dateReference);
      const dayOfMonth = dateReference.getUTCDate();

      const userInTime = weeklySchedule[dayNameOfWeek]?.inTime || "";
      const userOutTime = weeklySchedule[dayNameOfWeek]?.outTime || "";

      const inTime = formatUtcTime(session.in);
      const outTime = formatUtcTime(session.out);

      const sessionLogs = [
        ...(session.inRecords || []),
        ...(session.outRecords || []),
      ];

      const shouldCalculateOvertime = sessionLogs.some(
        (log) => log.calculateOverTime === false
      )
        ? false
        : true;

      const shouldCalculateLateTime = sessionLogs.some(
        (log) => log.calculateLateTime === false
      )
        ? false
        : true;

      const totalHoursValue =
        inTime && outTime
          ? parseFloat(getNightUserTotalHours(inTime, outTime) || 0.0)
          : 0.0;

      const totalLateTimeValue =
        inTime && outTime
          ? parseFloat(
              getNightUserTotalLateTime(
                inTime,
                outTime,
                userInTime,
                userOutTime,
                userData?.relaxationMinutes,
                rules,
                selectedDevice
              ) || 0.0
            )
          : 0.0;

      const overTimeValue =
        inTime && outTime
          ? parseFloat(
              getNightUserTotalOvertime(
                inTime,
                outTime,
                userInTime,
                userOutTime,
                userData?.overtimeAfterMinutes,
                rules,
                selectedDevice
              ) || 0.0
            )
          : 0.0;

      const totalExpectedHours =
        userInTime && userOutTime
          ? parseFloat(getNightUserTotalHours(userInTime, userOutTime) || 0.0)
          : 0.0;

      const formattedDate = `${
        dateReference.getUTCMonth() + 1
      }-${dateReference.getUTCDate()}-${dateReference.getUTCFullYear()}`;

      acc[formattedDate] = {
        inTime,
        outTime,
        shouldCalculateOvertime,
        shouldCalculateLateTime,
        formattedDate,
        dayOfWeek: dayOfMonth,
        inRecord: session.in,
        outRecord: session.out,
        dayNameOfWeek,
        userInTime,
        userOutTime,
        totalExpectedHours,
        totalLateTime: inTime === outTime ? 0.0 : totalLateTimeValue,
        totalHours: inTime === outTime ? 0.0 : totalHoursValue,
        overTime: inTime === outTime ? 0.0 : overTimeValue,
      };

      return acc;
    }, {});
  };

  const processUsersAttendanceData = (usersLogsData, holidaysList) => {
    // if (!usersLogsData || Object.keys(usersLogsData).length === 0) {
    //   dispatch(
    //     setNotification({
    //       message: "No attendance records found",
    //       type: "error",
    //     })
    //   );
    //   return;
    // }

    let recordMonthYear = selectedMonth?.split("-");
    let currentMonth = recordMonthYear[1];
    let currentYear = recordMonthYear[0];
    // // Get Month-Year of selected month (eg. 08-2024) Used for getting array of All days of month
    let monthYear = `${currentMonth}-${currentYear}`;

    // const selectedDate = new Date(selectedMonth);
    // const monthNameDate = selectedDate.toLocaleString("default", {
    //   month: "long",
    // });
    // const year = selectedDate.getFullYear();
    // console.log("selectedMonth", selectedMonth);
    // console.log("monthNameDate", monthNameDate);

    // Process each user's data
    const processedUsersData = Object.keys(usersLogsData)
      .map((userId) => {
        const userData = allUsers.find(
          (user) => user.userId === parseInt(userId)
        );
        const userLogs = usersLogsData[userId];

        if (!userData) {
          return null;
        }

        // Process the user's attendance records
        const recordsByDay = {};

        // Group logs by date
        userLogs.forEach((record) => {
          const date = new Date(record.recordTime);

          // Format time (HH:MM:SS)
          const hours = date.getUTCHours().toString().padStart(2, "0");
          const minutes = date.getUTCMinutes().toString().padStart(2, "0");
          const seconds = date.getUTCSeconds().toString().padStart(2, "0");

          const formattedTime = `${hours}:${minutes}:${seconds}`;
          const formattedDate = `${
            date.getUTCMonth() + 1
          }-${date.getUTCDate()}-${date.getUTCFullYear()}`;

          recordsByDay[formattedDate] = (
            recordsByDay[formattedDate] || []
          ).concat({
            ...record,
            formattedTime,
            formattedDate,
            dayOfWeek: getDayNameByDate(
              new Date(
                Date.UTC(
                  date.getUTCFullYear(),
                  date.getUTCMonth(),
                  date.getUTCDate()
                )
              )
            ),
          });
        });

        // Process each day's records
        const isNightOffice = userData?.isNightOffice || false;
        const parsedRecords = {};

        if (isNightOffice) {
          const nightRecords = getNightShiftRecordsByDate({
            logs: userLogs,
            userData,
          });
          Object.assign(parsedRecords, nightRecords);
        } else {
          Object.keys(recordsByDay).forEach((key) => {
            const records = recordsByDay[key];
            parsedRecords[key] = processDayOfficeRecords(records, userData);
          });
        }

        // Filter records to include only the selected month
        const filteredRecords = Object.entries(parsedRecords)
          .filter(([key]) => {
            const [month, day, year] = key.split("-").map(Number);
            // Fix: months are zero-based in JS Date
            const date = new Date(Date.UTC(year, month - 1, day));
            return (
              date.getUTCMonth() + 1 === parseInt(currentMonth) &&
              date.getUTCFullYear() === parseInt(currentYear)
            );
          })
          .reduce((obj, [key, value]) => {
            obj[key] = value;
            return obj;
          }, {});

        // Process holidays
        processHolidayData(
          filteredRecords,
          holidaysList,
          userData,
          currentMonth,
          currentYear
        );

        // Add entries for absent days
        addAbsentDaysData(filteredRecords, userData, monthYear);

        // Calculate summary statistics
        const summaryData = calculateSummary(filteredRecords);

        return {
          userData,
          attendanceRecords: filteredRecords,
          monthYear: monthYear,
          summaryData,
        };
      })
      .filter(Boolean); // Remove any null entries

    // if (processedUsersData.length === 0) {
    //   dispatch(
    //     setNotification({
    //       message: "No data available for the selected users in this month",
    //       type: "error",
    //     })
    //   );
    //   return;
    // }

    setSelectedReport({
      usersReports: processedUsersData,
      currentMonth,
      currentYear,
    });
  };

  // Helper function for processing day office records
  const processDayOfficeRecords = (arr, userData) => {
    if (!arr || arr.length === 0) {
      return {};
    }

    const sortedArr = arr.sort(
      (a, b) => new Date(a.recordTime) - new Date(b.recordTime)
    );

    let userRules = JSON.parse(userData?.rules || "{}");

    const firstEntry = sortedArr[0];
    const lastEntry = sortedArr[sortedArr.length - 1];

    const date = new Date(firstEntry.recordTime);
    let dayOfWeek = date.getDate();
    let formattedDate = firstEntry.formattedDate;
    let dayNameOfWeek = getDayNameByDate(date);

    const weeklySchedule = JSON.parse(userData?.weeklySchedule || "{}");
    const daySchedule = weeklySchedule[dayNameOfWeek] || {
      inTime: "00:00",
      outTime: "00:00",
    };

    let userInTime = daySchedule.inTime || "00:00";
    let userOutTime = daySchedule.outTime || "00:00";

    let inTime = firstEntry.formattedTime;
    let outTime = lastEntry.formattedTime;

    let relaxationMinutes = userData?.relaxationMinutes || 0;
    let overTimeAfterMinutes = userData?.overtimeAfterMinutes || 0;

    // Calculate total expected hours
    let totalExpectedHoursInDecimal =
      getTotalHours(userInTime, userOutTime) || 0.0;

    // Calculate late time
    let totalLateTimeValue =
      getTotalLateTime(
        inTime,
        outTime,
        userInTime,
        userOutTime,
        relaxationMinutes,
        userRules,
        selectedDevice
      ) || 0.0;

    // Calculate total hours worked
    let totalHoursValue = getTotalHours(inTime, outTime) || 0.0;

    // Calculate overtime
    let overTimeValue =
      getTotalOvertime(
        inTime,
        outTime,
        userInTime,
        userOutTime,
        overTimeAfterMinutes,
        userRules,
        selectedDevice
      ) || 0.0;

    let shouldCalculateOvertime =
      firstEntry.calculateOverTime === false ? false : true;

    // set overtime to 0 if calculateOvertime is false
    if (shouldCalculateOvertime === false) {
      overTimeValue = 0.0;
    }

    let shouldCalculateLateTime =
      firstEntry.calculateLateTime === false ? false : true;

    // set lateTime to 0 if calculateLateTime is false
    if (shouldCalculateLateTime === false) {
      totalLateTimeValue = 0.0;
    }

    return {
      inTime,
      outTime,
      shouldCalculateOvertime,
      shouldCalculateLateTime,
      formattedDate,
      dayOfWeek,
      inRecord: firstEntry.recordTime,
      outRecord: lastEntry.recordTime,
      dayNameOfWeek,
      userInTime,
      userOutTime,
      totalExpectedHours: totalExpectedHoursInDecimal,
      totalLateTime: inTime === outTime ? 0.0 : parseFloat(totalLateTimeValue),
      totalHours: inTime === outTime ? 0.0 : parseFloat(totalHoursValue),
      overTime: inTime === outTime ? 0.0 : parseFloat(overTimeValue),
    };
  };

  // Process holidays
  const processHolidayData = (
    filteredRecords,
    holidaysList,
    userData,
    currentMonth,
    currentYear
  ) => {
    if (!holidaysList || holidaysList.length === 0) {
      return;
    }

    const weeklySchedule = JSON.parse(userData?.weeklySchedule || "{}");

    const filteredHolidays = holidaysList.filter((holiday) => {
      const [year, month, day] = holiday.date
        .split("T")[0]
        .split("-")
        .map(Number);
      // Fix: months are zero-based in JS Date
      const holidayDate = new Date(Date.UTC(year, month - 1, day));
      return (
        holidayDate.getUTCMonth() + 1 === parseInt(currentMonth) &&
        holidayDate.getUTCFullYear() === parseInt(currentYear)
      );
    });

    filteredHolidays.forEach((holiday) => {
      const [year, month, day] = holiday.date
        .split("T")[0]
        .split("-")
        .map(Number);
      const dateObj = new Date(year, month - 1, day);
      const dayName = getDayNameByDate(dateObj);
      const daySchedule = weeklySchedule[dayName] || {
        working: false,
        inTime: "00:00",
        outTime: "00:00",
      };

      let totalExpectedHoursInDecimal = 0;
      if (daySchedule?.working) {
        const totalExpectedHours = calculateTimeDifference(
          daySchedule.outTime,
          daySchedule.inTime
        );
        totalExpectedHoursInDecimal = parseFloat(
          convertToDecimalHours(totalExpectedHours).toFixed(2)
        );
      }

      let correctDateFormat = `${month}-${day}-${year}`;

      /*   Check Overtime
       **************** */
      let overTime = 0.0;
      if (
        filteredRecords[correctDateFormat]?.shouldCalculateOvertime === false
      ) {
        overTime = 0.0;
      } else {
        overTime = filteredRecords[correctDateFormat]?.totalHours || 0.0;
      }

      filteredRecords[correctDateFormat] = {
        ...(filteredRecords[correctDateFormat] || {}),
        type: "Holiday",
        message: "Holiday",
        dayNameOfWeek: dayName,
        userInTime: daySchedule.inTime || "00:00",
        userOutTime: daySchedule.outTime || "00:00",
        totalExpectedHours: totalExpectedHoursInDecimal,
        totalLateTime: 0.0,
        overTime: overTime,
      };
    });
  };

  // Add absent days
  const addAbsentDaysData = (filteredRecords, userData, monthYear) => {
    const allMonthDates = getDatesArrByMonth(monthYear);
    const weeklySchedule = JSON.parse(userData?.weeklySchedule || "{}");

    allMonthDates.forEach((date) => {
      // Get day name
      let dateObj = new Date(date);
      let dayName = getDayNameByDate(dateObj);

      // Get schedule for this day
      let daySchedule = weeklySchedule[dayName] || { working: false };

      // Check if already marked as holiday
      if (filteredRecords[date]?.type === "Holiday") {
        return;
      }

      // Mark as holiday or absent
      if (!filteredRecords[date] && daySchedule?.working === false) {
        filteredRecords[date] = {
          message: "Holiday",
          totalLateTime: 0.0,
          dayNameOfWeek: dayName,
          userInTime: daySchedule.inTime || "00:00",
          userOutTime: daySchedule.outTime || "00:00",
        };
      } else if (
        (!filteredRecords[date] && daySchedule?.working === true) ||
        filteredRecords[date]?.message === "absent"
      ) {
        filteredRecords[date] = {
          message: "Absent",
          dayNameOfWeek: dayName,
          userInTime: daySchedule.inTime || "00:00",
          userOutTime: daySchedule.outTime || "00:00",
        };
      }
    });
  };

  // Calculate summary
  const calculateSummary = (attendanceRecords) => {
    return Object.values(attendanceRecords).reduce(
      (prev, curr) => {
        if (Object.keys(curr).length === 0) {
          return prev;
        }

        // Count absent days
        if (curr.message === "Absent") {
          prev.absentDaysCount += 1;
        }

        // Add totalLateTime, totalOverTime, totalWorkingHours
        prev.totalLateTime = curr.totalLateTime
          ? parseFloat((prev.totalLateTime + curr.totalLateTime).toFixed(2))
          : prev.totalLateTime;

        prev.totalOverTime = curr.overTime
          ? parseFloat((prev.totalOverTime + curr.overTime).toFixed(2))
          : prev.totalOverTime;

        prev.totalWorkingHours = curr.totalHours
          ? parseFloat((prev.totalWorkingHours + curr.totalHours).toFixed(2))
          : prev.totalWorkingHours;

        return prev;
      },
      {
        totalLateTime: 0.0,
        totalOverTime: 0.0,
        totalWorkingHours: 0.0,
        absentDaysCount: 0,
      }
    );
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (!deviceId) return;
      if (showModal) {
        getUsersDataByDeviceId(deviceId);
        GetDeviceById();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [GetDeviceById, deviceId, getUsersDataByDeviceId, showModal]);

  // Update filtered users whenever search term, department filter, or all users change
  useEffect(() => {
    const filtered = allUsers.filter((user) => {
      const matchesSearch = user.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesDepartment =
        filterDepartment === "" || user.department === filterDepartment;
      return matchesSearch && matchesDepartment;
    });
    setFilteredUsers(filtered);

    // Update selectAll checkbox state based on filtered users
    if (filtered.length > 0) {
      const allFilteredSelected = filtered.every((user) =>
        selectedUsers.includes(user.userId)
      );
      setSelectAll(allFilteredSelected);
    }
  }, [allUsers, searchTerm, filterDepartment, selectedUsers]);

  return (
    <>
      <button
        onClick={(e) => {
          setShowModal(true);
        }}
        className="btn btn-primary ms-1"
      >
        Monthly Attendance Report
      </button>
      <Modal
        contentClassName="bg-transparent"
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        size="lg"
      >
        <Modal.Body className="bg-white custom-border-radius px-4">
          <div>
            {loading && <Spinner />}
            <h3 className="mb-3">Generate Monthly Attendance Report</h3>

            <div>
              <label className="form-label">Select Month</label>
              <input
                className="form-control"
                type="month"
                value={selectedMonth || ""}
                onChange={handleSelectMonth}
              />
            </div>
            <div className="mt-1">
              <span className="fw-bold">Current Time Zone :</span>{" "}
              {selectedDevice?.timezone} <br />
              <span className="fw-bold">Current Time :</span>{" "}
              {getCurTimeOfDeviceTimeZone(selectedDevice)}
            </div>
            {!allUsers && <Spinner />}
            {allUsers.length > 0 && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Select Users</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ maxWidth: "200px" }}
                    />
                    <select
                      className="form-select form-select-sm"
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      style={{ maxWidth: "180px" }}
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                >
                  <table className="table table-sm table-hover mb-0">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <tr>
                        <th style={{ width: "40px" }}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectAll}
                              onChange={handleSelectAllUsers}
                              id="selectAllCheckbox"
                            />
                            <label
                              className="form-check-label"
                              htmlFor="selectAllCheckbox"
                            ></label>
                          </div>
                        </th>
                        <th>Name</th>
                        <th>Department</th>
                        <th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.userId}>
                          <td>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedUsers.includes(user.userId)}
                                onChange={() => handleSelectUser(user.userId)}
                                id={`user-${user.userId}`}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`user-${user.userId}`}
                              ></label>
                            </div>
                          </td>
                          <td>{user.name}</td>
                          <td>{user.department}</td>
                          <td>{user.userId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 text-muted small">
                  {selectedUsers.length} of {allUsers.length} users selected
                </div>
              </div>
            )}

            <div className="text-end mt-3">
              <button
                onClick={(e) => {
                  setShowModal(false);
                }}
                className="btn btn-sm btn-secondary"
              >
                Close
              </button>
              <button
                onClick={GenerateReport}
                disabled={
                  !selectedMonth || loading || selectedUsers.length === 0
                }
                type="button"
                className="btn btn-sm btn-info mx-1"
              >
                Get Records
              </button>
              {selectedReport && (
                <MonthlyAttReportAllUsersBtn
                  selectedReport={selectedReport}
                  btnClasses={"btn btn-primary btn-sm"}
                />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
